import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { RatesPanel } from "@/components/technicians/RatesPanel";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { AVAIL_STATUS, DAYS } from "@/lib/technicians";

const KV = ({ label, value, testid }) => (
  <div data-testid={testid}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-0.5 text-sm">{value || "—"}</div></div>
);
const Chips = ({ items, empty, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {items.length ? items.map((x) => <span key={x} className="rounded-md border border-border px-2 py-1 text-xs">{x}</span>) : <span className="text-sm text-muted-foreground">{empty}</span>}
  </div>
);

const History = ({ techId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/technicians/${techId}/history`).then((r) => setRows(r.data)); }, [techId]);
  if (!rows) return null;
  if (!rows.length) return <p className="text-sm text-muted-foreground">Sin OS asignadas.</p>;
  return (
    <div className="space-y-2" data-testid="tech-history-list">
      {rows.map((o) => (
        <Link key={o.id} to={`/admin/os/${o.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm hover:border-primary/40" data-testid={`tech-history-${o.folio}`}>
          <span className="font-mono text-primary">{o.folio}</span><StatusBadge status={o.status} />
          <span>{o.client_name}</span><span className="text-muted-foreground">{o.business_unit_name} · {o.service_type_name}</span>
          <span className="ml-auto text-xs text-muted-foreground">{fmtDate(o.closed_at || o.scheduled_date || o.created_at)}{!o.current && " · reasignada"}</span>
        </Link>
      ))}
    </div>
  );
};

export const TechnicianProfile = ({ techId, onOpenChange, onEdit, zones }) => {
  const { can } = useAuth();
  const [t, setT] = useState(null);
  useEffect(() => { setT(null); if (techId) api.get(`/technicians/${techId}`).then((r) => setT(r.data)); }, [techId]);
  const p = t?.technician_profile;
  const days = (p?.availability?.days || []).filter((d) => d.enabled);
  return (
    <Dialog open={!!techId} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="tech-profile-dialog">
        <DialogHeader><DialogTitle className="font-display">{t?.name || "Técnico"} <span className="ml-2 text-sm font-normal text-muted-foreground">{t?.type_label}</span></DialogTitle></DialogHeader>
        {t && (
          <Tabs defaultValue="datos">
            <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
              {[["datos", "Datos"], ["capacidades", "Especialidades y zonas"], ["disponibilidad", "Disponibilidad"], ...(can("rates.read") ? [["tarifas", "Tarifas"]] : []), ["historial", "Historial"]].map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`tech-profile-tab-${v}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="datos" className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <KV label="Teléfono" value={t.phone} /><KV label="Correo" value={t.email} /><KV label="Tipo" value={t.type_label} testid="tech-profile-type" />
                {p.type === "EXTERNAL" && <><KV label="Empresa / proveedor" value={p.company} /><KV label="RFC" value={p.rfc} /></>}
                <KV label="Estado" value={t.active ? "Activo" : "Archivado"} />
                <KV label="OS abiertas" value={`${t.workload.abiertas} (en proceso ${t.workload.en_proceso} · pend. cierre ${t.workload.pendiente_cierre} · programadas ${t.workload.programadas})`} testid="tech-profile-workload" />
              </div>
              {can("users.write") && <Button variant="outline" size="sm" onClick={() => onEdit(t)} data-testid="tech-profile-edit-button"><Pencil className="mr-1 h-3.5 w-3.5" />Editar perfil</Button>}
            </TabsContent>
            <TabsContent value="capacidades" className="space-y-4">
              <KV label="Especialidades" value={<Chips items={t.specialties.map((s) => s.name)} empty="Sin especialidades" testid="tech-profile-specialties" />} />
              <KV label="Zonas de cobertura" value={<Chips items={t.zones.map((z) => z.path)} empty="Sin zonas" testid="tech-profile-zones" />} />
            </TabsContent>
            <TabsContent value="disponibilidad" className="space-y-3" data-testid="tech-profile-availability">
              <KV label="Estado" value={AVAIL_STATUS[p.availability?.status] || "Disponible"} />
              {days.length ? days.map((d) => <div key={d.day} className="text-sm">{DAYS[d.day]}: {d.start || "—"}–{d.end || "—"}</div>) : <p className="text-sm text-muted-foreground">Sin horario configurado</p>}
            </TabsContent>
            {can("rates.read") && <TabsContent value="tarifas"><RatesPanel techId={t.id} zones={zones} /></TabsContent>}
            <TabsContent value="historial"><History techId={t.id} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
